import { useEffect, useState } from 'react';

import * as api from '@/features/backlog/api';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/components/ui/cn';

/** The attachment id embedded in one of our attachment API URLs (or a bare uuid), else null for an
 *  external image the browser can load directly. Used to decide whether a token-authed fetch is
 *  needed (our API 401s a plain <img>) vs. a normal <img src>. */
export const attachmentIdFromSrc = (src: string): string | null => {
  if (!src) return null;
  const marker = '/attachments/';
  const i = src.indexOf(marker);
  if (i !== -1) {
    const rest = src.slice(i + marker.length).split(/[?#/]/)[0];
    if (/^[0-9a-f-]{16,}$/i.test(rest)) return rest;
  }
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(src)) return src;
  return null;
};

/**
 * An <img> that renders an attachment stored in the bucket. Attachments are served through the API,
 * which requires the Bearer token — a bare <img src> would 401 — so we fetch the bytes through axios
 * (token attached) and show them via an object URL, revoked on unmount. Non-attachment src values
 * (external URLs) load directly. Used both in the description editor and its read-only rendering.
 */
/** Fetches an attachment's bytes with auth (a bare <img>/<video> would 401), exposing an object URL
 *  plus the blob's own MIME type so a caller can pick <img> vs <video>. A non-attachment (external)
 *  src is used verbatim with an empty type. The object URL is revoked on unmount / src change. */
function useAuthedBlob(src: string) {
  const [state, setState] = useState<{ url: string | null; type: string; error: boolean }>({ url: null, type: '', error: false });
  useEffect(() => {
    const id = attachmentIdFromSrc(src);
    if (!id) {
      setState({ url: src, type: '', error: false });
      return;
    }
    let cancelled = false;
    let obj: string | null = null;
    setState({ url: null, type: '', error: false });
    api
      .fetchAttachmentBlob(id)
      .then((blob) => {
        if (cancelled) return;
        obj = URL.createObjectURL(blob);
        setState({ url: obj, type: blob.type || '', error: false });
      })
      .catch(() => {
        if (!cancelled) setState({ url: null, type: '', error: true });
      });
    return () => {
      cancelled = true;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [src]);
  return state;
}

export default function AuthImage({ src, alt, className }: { src: string; alt?: string; className?: string }) {
  const { url, error } = useAuthedBlob(src);
  if (error)
    return (
      <span className={cn('inline-flex items-center gap-1.5 rounded-md bg-surface-inset px-2 py-1 text-[12px] text-fg-faint', className)}>
        Image unavailable
      </span>
    );
  if (!url)
    return (
      <span className={cn('inline-flex h-24 w-40 items-center justify-center rounded-lg bg-surface-inset', className)}>
        <Spinner className="h-4 w-4 text-fg-faint" />
      </span>
    );
  return <img src={url} alt={alt ?? ''} className={className} />;
}

const mediaClass = 'max-h-[440px] max-w-full rounded-lg ring-1 ring-line';

/**
 * Renders an attachment as inline media in the description: a token-authed <video controls> when the
 * fetched blob is a video, otherwise a zoomable <img>. Deciding from the blob's OWN MIME type means it
 * needs no attachments-list lookup and never briefly shows the wrong element (image vs video).
 */
export function AuthMedia({ src, alt, onImageClick }: { src: string; alt?: string; onImageClick?: () => void }) {
  const { url, type, error } = useAuthedBlob(src);
  // The <video>/<img> can load its bytes yet still fail to DECODE (e.g. an iPhone HEVC .mov the browser
  // can't play) — the fetch succeeds so `error` never fires. Catch the element's onError and fall back
  // to a download link instead of a silent dead player. Reset when the src changes.
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  // Prefer the fetched blob's MIME; an external (non-attachment) src has no blob type, so sniff the
  // file extension so a raw ![](clip.mp4) still renders as a video rather than a broken image.
  const isVideo = type.startsWith('video/') || (!type && /\.(mp4|webm|ogg|mov|m4v)(\?|#|$)/i.test(src));
  if (error || failed)
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md bg-surface-inset px-2 py-1 text-[12px] text-fg-faint">
        {isVideo ? 'This video can’t be previewed here' : 'Media unavailable'}
        {url ? (
          <a href={url} download className="text-fg-soft underline underline-offset-2">
            download
          </a>
        ) : null}
      </span>
    );
  if (!url)
    return (
      <span className="inline-flex h-24 w-40 items-center justify-center rounded-lg bg-surface-inset">
        <Spinner className="h-4 w-4 text-fg-faint" />
      </span>
    );
  if (isVideo) return <video src={url} controls playsInline preload="metadata" onError={() => setFailed(true)} className={mediaClass} />;
  return (
    <span className="inline-block cursor-zoom-in" onClick={onImageClick}>
      <img src={url} alt={alt ?? ''} onError={() => setFailed(true)} className={mediaClass} />
    </span>
  );
}
